import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { StarRating } from "./StarRating";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ThumbsUp } from "lucide-react";

const PAGE_SIZE = 10;

export const ReviewSection = ({ targetType, targetId }) => {
  const { t } = useI18n();
  const { user } = useAuth();
  const [data, setData] = useState({ items: [], total: 0, breakdown: {1:0,2:0,3:0,4:0,5:0} });
  const [page, setPage] = useState(1);
  const [loaded, setLoaded] = useState(false);
  const [sort, setSort] = useState("recent");
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Page 1 replaces the list; later pages append ("show more").
  const load = useCallback(async (p = 1, signal) => {
    try {
      const r = await api.get(`/reviews?target_type=${targetType}&target_id=${targetId}&sort=${sort}&page=${p}&limit=${PAGE_SIZE}`, { signal });
      setData(d => p === 1 ? r.data : { ...r.data, items: [...d.items, ...r.data.items] });
      setPage(p);
      setLoaded(true);
    } catch (e) {
      if (!signal?.aborted) toast.error(t("error_title"));
    }
  }, [targetType, targetId, sort, t]);
  useEffect(() => {
    if (!targetId) return undefined;
    const ctrl = new AbortController();
    load(1, ctrl.signal);
    return () => ctrl.abort();
  }, [targetId, load]);

  const submit = async () => {
    if (!user) { toast.error(t("login_required")); return; }
    if (!rating) return;
    setSubmitting(true);
    try {
      await api.post("/reviews", { target_type: targetType, target_id: targetId, rating, text });
      setText(""); await load();
      toast.success(t("review_published"));
    } catch (e) { toast.error(t("error_title")); }
    finally { setSubmitting(false); }
  };

  const toggleHelpful = async (rid) => {
    if (!user) { toast.error(t("login_required")); return; }
    try { await api.post(`/reviews/${rid}/helpful`); await load(1); } catch { toast.error(t("error_title")); }
  };

  // Averages come from the full breakdown, not just the reviews loaded so far.
  const counted = Object.values(data.breakdown).reduce((a, b) => a + b, 0);
  const total = counted || 1;
  const average = counted ? Object.entries(data.breakdown).reduce((s, [n, c]) => s + Number(n) * c, 0) / counted : 0;

  return (
    <section className="space-y-6" data-testid="reviews-section">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <h2 className="text-[1.6rem] sm:text-[2rem]">{t("reviews")}</h2>
        <div className="flex gap-2 text-sm">
          <button onClick={() => setSort("recent")} className={`px-3 py-1 rounded-sm border ${sort==="recent"?"bg-primary text-primary-foreground border-primary":"border-border"}`} data-testid="sort-recent">{t("sort_newest")}</button>
          <button onClick={() => setSort("helpful")} className={`px-3 py-1 rounded-sm border ${sort==="helpful"?"bg-primary text-primary-foreground border-primary":"border-border"}`} data-testid="sort-helpful">{t("helpful")}</button>
        </div>
      </div>

      {/* Until the first load, hold roughly the loaded height so the page does not jump. */}
      {!loaded && <div className="h-56 lg:h-36 skel" aria-hidden="true" data-testid="reviews-loading" />}

      {/* Breakdown (only once there is something to break down) */}
      {counted > 0 && <div className="border border-border rounded-md p-5 bg-card">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
          <div className="text-center">
            <div className="font-display text-5xl text-primary">
              {counted ? average.toFixed(1) : "—"}
            </div>
            <div className="mt-2 flex justify-center">
              <StarRating value={average} size={18} />
            </div>
            <div className="text-xs text-muted-foreground mt-1">{data.total} {t("reviews")}</div>
          </div>
          <div className="md:col-span-2 space-y-1">
            {[5,4,3,2,1].map(n => {
              const c = data.breakdown[n] || 0;
              const pct = Math.round((c/total)*100);
              return (
                <div key={n} className="flex items-center gap-3 text-xs" data-testid={`breakdown-${n}`}>
                  <span className="w-8 text-muted-foreground">{n} ★</span>
                  <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-accent" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="w-10 text-right text-muted-foreground">{c}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>}

      {/* Write review */}
      {user ? (
        <div className="border border-border rounded-md p-5 bg-card space-y-3" data-testid="write-review-box">
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">{t("your_rating")}</span>
            <StarRating value={rating} size={22} interactive onChange={setRating} />
          </div>
          <Textarea placeholder={t("review_placeholder")} aria-label={t("write_review")} value={text} onChange={e=>setText(e.target.value)} rows={4} data-testid="review-text-input" />
          <Button onClick={submit} disabled={submitting} data-testid="submit-review-btn">{t("submit")}</Button>
        </div>
      ) : (
        <div className="border border-dashed border-border rounded-md p-5 text-center text-sm text-muted-foreground" data-testid="login-required-review">
          {t("login_required")}
        </div>
      )}

      {/* List */}
      <div className="space-y-4" data-testid="review-list">
        {data.items.length === 0 && <div className="text-sm text-muted-foreground italic">{t("no_reviews")}</div>}
        {data.items.map(r => (
          <div key={r.id} className="py-4 border-b border-border/60 last:border-0" data-testid={`review-${r.id}`}>
            <div className="flex justify-between items-start mb-2">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center overflow-hidden">
                  {r.user_avatar ? <img src={r.user_avatar} alt="" className="w-full h-full object-cover" /> : <span aria-hidden="true">{r.user_name?.[0] || "U"}</span>}
                </div>
                <div>
                  <div className="text-sm font-medium">{r.user_name}</div>
                  <div className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}</div>
                </div>
              </div>
              <StarRating value={r.rating} size={14} />
            </div>
            {r.text && <p className="reading whitespace-pre-line text-foreground/90">{r.text}</p>}
            <div className="mt-3">
              <button onClick={()=>toggleHelpful(r.id)} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary" data-testid={`helpful-${r.id}`}>
                <ThumbsUp size={12} /> {t("helpful")} ({r.helpful_count || 0})
              </button>
            </div>
          </div>
        ))}
      </div>
      {data.items.length < data.total && (
        <button onClick={() => load(page + 1)} className="w-full border border-border rounded-sm py-2 text-sm hover:border-primary/50" data-testid="more-reviews">
          {t("more_reviews")} ({data.total - data.items.length})
        </button>
      )}
    </section>
  );
};
